import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import {
  AttachmentPreviewList,
  SendButton as DefaultSendButton,
  SimpleAttachmentSelector,
  TextareaComposer,
  TextareaComposerProps,
  useComponentContext,
  useMessageComposer,
  useMessageInputContext,
  WithDragAndDropUpload,
} from 'stream-chat-react';

import { ErrorText } from 'src/components/ErrorText';
import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import {
  MAX_CHARACTERS_LIMIT,
  MAX_CHARACTERS_LIMIT_ERROR,
} from 'src/features/channel/channel-content/ChannelContent';
import { CustomAttachmentPreview } from 'src/features/channel/custom-attachments/CustomAttachmentPreview';
import { AtMentionButton } from 'src/features/channel/custom-message-input/ActionButtons';
import { MessageRichTextInput } from 'src/features/channel/custom-message-input/MessageRichTextInput';
import {
  FileDropHintColor,
  useMessageInputDropZone,
} from 'src/features/channel/custom-message-input/useMessageInputDropZone';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { useOSDetector } from 'src/hooks/useOSDetector';

const TextareaComposerWrapper = (props: TextareaComposerProps) => {
  const { textareaRef } = useMessageInputContext();

  // there's a known bug in the autoresize text area library that GetStream uses
  // that causes it to grow to full height when it enters and leaves the viewport
  // https://github.com/Andarist/react-textarea-autosize/issues/393
  // this code detects when it enters the viewport and forces the component to rerender
  const [_, setForceRerender] = useState(false);
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setForceRerender((prev) => !prev);
        }
      },
      { root: null, rootMargin: '0px' },
    );

    if (textareaRef.current) {
      intersectionObserver.observe(textareaRef.current);
    }

    return () => intersectionObserver.disconnect();
  }, [textareaRef]);

  // we also need to force the textarea to rerender whenever we resize it
  // since the autoresize component under the hood only accounts for changes
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setForceRerender((prev) => !prev);
    });

    if (textareaRef.current) {
      resizeObserver.observe(textareaRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [textareaRef]);

  return <TextareaComposer {...props} />;
};

// created while referencing the default MessageInputFlat component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageInput/MessageInputFlat.tsx
export const CustomMessageInput = () => {
  const EnableMessageEditorInput = useFeatureFlagEnabled(
    'message-editor-input',
  );
  const { handleSubmit, textareaRef, additionalTextareaProps } =
    useMessageInputContext();
  const { textComposer, attachmentManager } = useMessageComposer();
  const { SendButton = DefaultSendButton } = useComponentContext();
  const { isOSDeleteKeyPressed } = useOSDetector();
  const handleOnKeyDownCapture = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (isOSDeleteKeyPressed(event)) {
      event.stopPropagation();
    }
    // Block Enter key submission when character limit is exceeded
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      textComposer.text.length > MAX_CHARACTERS_LIMIT
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const { isSidebarTheme } = useMessagingTheme();
  const { isInternalChat } = useInternalChatContext();
  const [fileDropHintColor, setFileDropHintColor] =
    useState<FileDropHintColor | null>(null);
  const { messageInputRef } = useMessageInputDropZone({
    isInternalChat,
    attachmentManager,
    setFileDropHintColor,
  });

  // Store the TipTap editor reference for the @ mention button
  const tiptapEditorRef = useRef<Editor | null>(null);

  const [messageContent, setMessageContent] = useState(textComposer.text);
  const [editorComposerHasLimitReached, setEditorComposerHasLimitReached] =
    useState(false);
  const handleMessageContentChange = async (editor: Editor) => {
    const textContent = editor.getText();
    // override the submit handler to use the new content
    await textComposer.handleChange({
      selection: {
        end: editor.state.selection.$to.pos,
        start: editor.state.selection.$from.pos,
      },
      text: editor.storage.markdown.getMarkdown() ?? '',
    });
    if (textContent.length > MAX_CHARACTERS_LIMIT) {
      setEditorComposerHasLimitReached(true);
    } else {
      setEditorComposerHasLimitReached(false);
    }

    setMessageContent(textContent);
  };

  const handleSendMessage = () => {
    handleSubmit();
    textComposer.setText('');
    setMessageContent('');
  };

  const focusOnContainerClick = () => {
    if (EnableMessageEditorInput && tiptapEditorRef.current) {
      tiptapEditorRef.current.commands.focus();
    } else {
      textareaRef.current?.focus();
    }
  };

  return (
    <>
      {editorComposerHasLimitReached && (
        <div className="px-4">
          <ErrorText>{MAX_CHARACTERS_LIMIT_ERROR}</ErrorText>
        </div>
      )}
      <WithDragAndDropUpload>
        <div
          ref={messageInputRef}
          className={clsx(
            isSidebarTheme ? 'px-4' : 'pl-4 pr-9',
            isInternalChat ? 'pb-3' : 'py-3',
          )}
        >
          <div
            className={clsx(
              'border border-solid border-non-hover-border active:border-black-heading focus-within:border-black-heading rounded-xl cursor-text',
              fileDropHintColor
                ? fileDropHintColor
                : isSidebarTheme
                  ? 'bg-gray-100'
                  : 'bg-white',
              EnableMessageEditorInput ? 'p-0' : 'px-3 py-2',
            )}
            role="textbox"
            tabIndex={0}
            onClick={focusOnContainerClick}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                event.target !== textareaRef.current
              ) {
                event.preventDefault();
                textareaRef.current?.focus();
              }
            }}
          >
            {EnableMessageEditorInput ? (
              <div>
                <MessageRichTextInput
                  handleSendMessage={handleSendMessage}
                  content={messageContent}
                  onContentChange={handleMessageContentChange}
                  editorRef={(editor) => {
                    tiptapEditorRef.current = editor;
                  }}
                  placeholder={additionalTextareaProps?.placeholder}
                />
                {/* We intentionally moved the attachment preview
              below the message editor because it looks weird
              if it displays above the editor. Below looks better. */}
                <AttachmentPreviewList
                  UnsupportedAttachmentPreview={CustomAttachmentPreview}
                />
              </div>
            ) : (
              <>
                <AttachmentPreviewList
                  UnsupportedAttachmentPreview={CustomAttachmentPreview}
                />
                <TextareaComposerWrapper
                  className="w-full border-none resize-none overflow-x-hidden p-0 mb-2 text-base placeholder-light-gray bg-inherit"
                  containerClassName="relative" // this allows the suggestion list to be correctly positioned
                  name="message input"
                  onKeyDownCapture={handleOnKeyDownCapture}
                />
              </>
            )}

            <div
              className={clsx(
                'flex items-center justify-between',
                EnableMessageEditorInput ? 'px-3 py-2' : 'p-0',
              )}
            >
              <div className="flex items-center gap-1.5">
                {isInternalChat && (
                  <AtMentionButton
                    onClick={() => {
                      if (EnableMessageEditorInput && tiptapEditorRef.current) {
                        tiptapEditorRef.current
                          .chain()
                          .focus()
                          .insertContent('@')
                          .run();
                      } else {
                        textareaRef.current?.focus();
                        void textComposer.insertText({ text: '@' });
                      }
                    }}
                  />
                )}
                <SimpleAttachmentSelector />
              </div>
              <SendButton sendMessage={handleSendMessage} />
            </div>
          </div>
        </div>
      </WithDragAndDropUpload>
    </>
  );
};
